import { IconX } from "@tabler/icons-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import sky from "../../assets/images/juiscigpt-wizard-sky.jpg";
import type { Api } from "../../services/api";
import { WIZARD_DARK_SPRING, WIZARD_GLIDE } from "./wizardMotion";

type WizardQuestion = NonNullable<
  Extract<
    Api["get"]["/webapp/private/users/me/juiscigpt-wizard"]["output"]["body"],
    { status: "none" }
  >["question"]
>;

/**
 * The invitation card of gate A (Figma A1, V7 prototype "welcome",
 * docs/juiscigpt-wizard.md REQ-01): a dark translucent card at the bottom of
 * the blurred JuisciGPT home, with a preview of a question and its answer, the
 * tour question, and the two choices stacked. The close control and a tap
 * outside the card are "Plus tard" (REQ-11).
 */
export function WizardInvitation({
  question,
  onStart,
  onLater,
}: {
  question: WizardQuestion;
  onStart: () => void;
  onLater: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion() === true;
  const label =
    question.level === "specialty"
      ? t("A question chosen for your specialty")
      : t("A question of the moment");
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ds-moss-600/30 px-3 backdrop-blur-sm"
      style={{
        paddingTop: "calc(var(--safe-area-top) + 1rem)",
        paddingBottom: "calc(var(--safe-area-bottom) + 1.25rem)",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={reducedMotion ? { duration: 0 } : WIZARD_GLIDE}
      onClick={onLater}
    >
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-labelledby="juiscigpt-wizard-invitation-title"
        // A small phone shows a long question: the card keeps to the screen
        // and its content scrolls, so the close control stays reachable.
        className="flex max-h-full w-full max-w-md flex-col gap-2 overflow-y-auto overscroll-contain rounded-4xl bg-ds-moss-950/84 p-3 pb-4 text-ds-canvas shadow-2xl ring-1 ring-ds-white/6 backdrop-blur-md"
        initial={reducedMotion ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reducedMotion ? { duration: 0 } : WIZARD_DARK_SPRING}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <header className="flex min-h-9 items-center justify-between gap-2 pl-2">
          <span className="text-xs font-medium tracking-wide opacity-72">
            {t("Guided tour · 2 min")}
          </span>
          <button
            type="button"
            aria-label={t("Later")}
            className="flex size-8 items-center justify-center rounded-full bg-ds-white/8 ring-1 ring-ds-white/14"
            onClick={onLater}
          >
            <IconX className="size-4" stroke={1.8} />
          </button>
        </header>
        {/* A static preview of an answer (V7 prototype). Its medical content
            needs the review of the medical team before the launch. */}
        <div
          aria-hidden="true"
          className="relative h-48 shrink-0 overflow-hidden rounded-3xl bg-cover bg-center"
          style={{ backgroundImage: `url(${sky})` }}
        >
          <div className="absolute right-4 top-4 flex w-52 flex-col items-end gap-1 text-xs">
            <span className="rounded-full bg-ds-white/84 px-2 font-bold text-ds-blackberry-800">
              {t("You")}
            </span>
            <p className="rounded-2xl rounded-br-sm bg-ds-black/32 px-4 py-3 text-ds-canvas backdrop-blur-md">
              {t("Cholecystitis at 72: early or delayed surgery?")}
            </p>
          </div>
          <div className="absolute bottom-4 left-4 flex w-64 flex-col items-start gap-1 text-xs">
            <span className="rounded-full bg-ds-white/84 px-2 font-bold text-ds-mint-800">
              JuisciGPT
            </span>
            <p className="rounded-2xl rounded-bl-sm bg-ds-white/32 px-3 py-2 font-medium text-ds-slate-900 backdrop-blur-md">
              {t(
                "Early (< 72 h), if the condition allows. WSES 2020, Tokyo 2018."
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 px-2 pt-2">
          <h2
            id="juiscigpt-wizard-invitation-title"
            className="mt-2 text-2xl font-semibold leading-tight text-balance"
          >
            {t("JuisciGPT answers your clinical questions")}
          </h2>
          <p className="text-base leading-snug opacity-86">
            {t(
              "An AI that searches society guidelines and publications, and cites its sources."
            )}
          </p>
          <div className="mt-1 flex flex-col gap-1 rounded-2xl border border-ds-white/14 bg-ds-white/8 px-3.5 py-3">
            <span className="text-xs opacity-66">{label}</span>
            <p className="text-lg font-semibold leading-snug">
              {question.text}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-1 px-2 pt-1.5">
          <button
            type="button"
            className="h-14 w-full rounded-xl bg-ds-moss-300 text-base font-medium text-ds-moss-950 ring-1 ring-ds-black/30"
            onClick={onStart}
          >
            {t("Start the tour")}
          </button>
          <button
            type="button"
            className="h-14 w-full text-base font-medium text-ds-canvas"
            onClick={onLater}
          >
            {t("Later")}
          </button>
        </div>
      </motion.section>
    </motion.div>
  );
}
